import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  Tooltip,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material'
import { Add, Edit, Delete, Send } from '@mui/icons-material'
import { useEmailManagement } from '../../../hooks/useEmailManagement'
import EmailTemplateDialog from '../dialogs/EmailTemplateDialog'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { getEmailTypeLabel } from '../../../utils/emailTypeOptions'
import { sortTemplatesByDisplayOrder } from '../../../utils/templateOrder'

const EmailTemplatesTable = ({ showNotification, onUseTemplate }) => {
  const {
    templates,
    loading,
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useEmailManagement()
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  })

  const orderedTemplates = useMemo(
    () => sortTemplatesByDisplayOrder(templates),
    [templates],
  )

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  const handleCreateClick = () => {
    setSelectedTemplate(null)
    setOpenDialog(true)
  }

  const handleEditClick = (template) => {
    setSelectedTemplate(template)
    setOpenDialog(true)
  }

  const handleDeleteClick = (template) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to delete template "${template.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteTemplate(template.id)
          showNotification('Template deleted successfully', 'success')
        } catch (error) {
          showNotification(error.message || 'Delete failed', 'error')
        } finally {
          setConfirmDialog({ ...confirmDialog, open: false })
        }
      },
    })
  }

  const handleUseTemplate = (template) => {
    if (typeof onUseTemplate === 'function') {
      onUseTemplate(template)
      showNotification(`Template "${template.name}" loaded in Compose tab.`, 'success')
      return
    }

    showNotification(
      `Template "${template.name}" selected. Switch to Compose tab to use it.`,
      'info'
    )
  }

  const handleSubmit = async (templateData) => {
    try {
      if (selectedTemplate) {
        await updateTemplate(selectedTemplate.id, templateData)
        showNotification('Template updated successfully', 'success')
      } else {
        await createTemplate(templateData)
        showNotification('Template created successfully', 'success')
      }
      setOpenDialog(false)
    } catch (error) {
      showNotification(error.message || 'Operation failed', 'error')
      throw error
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          Email Templates
        </Typography>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={handleCreateClick}
          sx={{
            bgcolor: '#232536',
            color: 'white',
            '&:hover': { bgcolor: '#1a1d28' },
            px: 3,
            py: 1,
          }}
        >
          Create Template
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ boxShadow: 1, borderRadius: 2 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                {/* <TableCell sx={{ fontWeight: 600 }}>Content Type</TableCell> */}
                <TableCell sx={{ fontWeight: 600 }}>Email Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Usage Count</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 140 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                    <Typography color='text.secondary'>
                      No templates found. Create your first template to get
                      started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orderedTemplates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>{template.subject}</TableCell>
                    {/* <TableCell>
                      <Chip
                        label={template.contentType || 'HTML'}
                        size='small'
                        color={
                          template.contentType === 'MARKDOWN'
                            ? 'primary'
                            : 'default'
                        }
                      />
                    </TableCell> */}
                    <TableCell>
                      <Chip
                        label={getEmailTypeLabel(template.emailType)}
                        size='small'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>{template.usageCount || 0}</TableCell>
                    <TableCell>
                      {template.lastUsedAt
                        ? new Date(template.lastUsedAt).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title='Use Template'>
                          <IconButton
                            size='small'
                            onClick={() => handleUseTemplate(template)}
                            title='Use Template'
                            sx={{ color: '#232536' }}
                          >
                            <Send fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Edit Template'>
                          <IconButton
                            size='small'
                            onClick={() => handleEditClick(template)}
                            title='Edit Template'
                            sx={{ color: '#232536' }}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete Template'>
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteClick(template)}
                            title='Delete Template'
                            sx={{ color: 'error.main' }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EmailTemplateDialog
        open={openDialog}
        template={selectedTemplate}
        onClose={() => setOpenDialog(false)}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
      />
    </Box>
  )
}

export default EmailTemplatesTable
